import React from 'react';
import { Box, Grid, Text, VStack, HStack, Icon } from '@chakra-ui/react';
import { Network, FlaskConical } from 'lucide-react';
import { SectionContainer } from '../SectionContainer';
import { ScrollReveal } from '../ScrollReveal';

const cards = [
  {
    icon: Network,
    kicker: 'ORCHESTRATION OVER MODELS',
    body:
      'Any model can answer a question. Few systems can decompose a goal into hundreds of coordinated steps, run them across specialized agents, and assemble the result. That orchestration — not the model — is where value is created. As models get better, yetzira.ai simply runs better on the same architecture.',
  },
  {
    icon: FlaskConical,
    kicker: 'LEARNING PAST THE CUTOFF',
    body:
      'Every model has a knowledge cutoff and retrains only periodically — always months behind reality. yetzira.ai documents what it learns in real time. It experiments, records what works, and compounds that knowledge daily — staying ahead of the frontier instead of waiting for the next model to catch up.',
  },
];

function MoatCard({ card, index }) {
  return (
    <ScrollReveal delay={index * 0.15}>
      <VStack
        align="flex-start"
        spacing={4}
        bg="bg.700"
        border="1px solid"
        borderColor="whiteAlpha.100"
        borderRadius="18px"
        p={{ base: '28px', md: '36px' }}
        h="100%"
        _hover={{
          borderColor: 'brand.500',
          boxShadow: '0 0 0 1px rgba(99,102,241,0.3), 0 8px 32px rgba(99,102,241,0.12)',
        }}
        sx={{ transition: 'all 0.25s ease' }}
        role="article"
      >
        <Box
          w="48px"
          h="48px"
          borderRadius="12px"
          bg="rgba(56,189,248,0.1)"
          display="flex"
          alignItems="center"
          justifyContent="center"
          aria-hidden="true"
        >
          <Icon as={card.icon} color="brand.400" boxSize={6} />
        </Box>
        <Text
          fontFamily="'JetBrains Mono', monospace"
          fontWeight="500"
          fontSize="0.75rem"
          textTransform="uppercase"
          letterSpacing="1.5px"
          color="brand.300"
        >
          {card.kicker}
        </Text>
        <Text color="text.muted" fontSize="1rem" lineHeight="1.7">
          {card.body}
        </Text>
      </VStack>
    </ScrollReveal>
  );
}

export default function MoatSection() {
  return (
    <SectionContainer bg="bg.900" id="moat" aria-labelledby="moat-heading">
      <VStack spacing={{ base: 10, md: 14 }} align="stretch">
        <ScrollReveal>
          <VStack align="flex-start" spacing={4} maxW="760px">
            <Text
              fontFamily="'JetBrains Mono', monospace"
              fontWeight="500"
              fontSize="0.75rem"
              textTransform="uppercase"
              letterSpacing="1.5px"
              color="brand.300"
            >
              The Moat
            </Text>
            <Text
              id="moat-heading"
              as="h2"
              fontFamily="'Space Grotesk', sans-serif"
              fontWeight="700"
              fontSize={{ base: 'clamp(1.8rem, 5vw, 2rem)', md: 'clamp(2rem, 3.5vw, 2.75rem)' }}
              letterSpacing="-0.02em"
              color="text.primary"
              lineHeight="1.15"
            >
              The model isn't the moat.
            </Text>
            <Text fontSize="1.1rem" lineHeight="1.7" color="text.muted">
              Large language models are becoming a commodity — interchangeable, ever-cheaper
              fuel. yetzira.ai is built so the fuel never matters.{' '}
              <Box as="span" color="text.primary" fontWeight="600">
                The engine does.
              </Box>
            </Text>
          </VStack>
        </ScrollReveal>

        <Grid
          templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }}
          gap={{ base: 5, md: 6 }}
        >
          {cards.map((card, i) => (
            <MoatCard key={card.kicker} card={card} index={i} />
          ))}
        </Grid>

        {/* Closing punchline */}
        <ScrollReveal delay={0.2}>
          <HStack
            justify="center"
            borderTop="1px solid"
            borderColor="whiteAlpha.100"
            pt={{ base: 8, md: 12 }}
          >
            <Text
              fontFamily="'Space Grotesk', sans-serif"
              fontWeight="700"
              fontStyle="italic"
              fontSize={{ base: '1.4rem', md: '2rem' }}
              letterSpacing="-0.02em"
              color="brand.400"
              textAlign="center"
            >
              Models are rented. Learning is owned.
            </Text>
          </HStack>
        </ScrollReveal>
      </VStack>
    </SectionContainer>
  );
}
